import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Page, Header, Content, InfoCard } from '@backstage/core-components';
import { Box, Button, CircularProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

// Format date string to readable format: "Jan 15, 2026, 02:30 PM"
const formatExecutionDate = (dateString: string | null): string => {
  if (!dateString) return 'Test Report';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return 'Test Report';
  }
};

export const AllureReportPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const project = searchParams.get('project');
  const reportId = searchParams.get('report');
  const runCreatedAt = searchParams.get('runCreatedAt');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!project || !reportId) {
      setError('Missing project or report ID');
      setLoading(false);
      return;
    }

    setLoading(false);
  }, [project, reportId]);

  const handleBack = () => {
    navigate('/qa-automated-tests');
  };

  if (!project || !reportId) {
    return (
      <Page themeId="tool">
        <Header title="Allure Report" subtitle="Invalid parameters" />
        <Content>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <Alert severity="error">
              Missing required parameters. Please navigate from the QA Automated Tests page.
            </Alert>
          </Box>
        </Content>
      </Page>
    );
  }

  // Build iframe URL using backend proxy
  const iframeUrl = `/api/proxy/allure/projects/${project}/reports/${reportId}/index.html`;

  return (
    <Page themeId="tool">
      <Header 
        title={`Allure Report - ${project}`} 
        subtitle={`Report #${reportId}`}
      >
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back to Workflow Runs
        </Button>
      </Header>
      <Content>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" style={{ marginBottom: 16 }}>
            {error}
          </Alert>
        )}
        
        {!loading && !error && (
          <InfoCard title={formatExecutionDate(runCreatedAt)}>
            <Box position="relative" width="100%" height="calc(100vh - 250px)" minHeight="600px">
              <iframe
                src={iframeUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                title={`Allure Report - ${project} - ${reportId}`}
              />
            </Box>
          </InfoCard>
        )}
      </Content>
    </Page>
  );
};
